import { IntlShape, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { Component } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation, useNavigate } from 'react-router-dom';

import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import wonItemsFilterOptions from '@/enums/wonItemsFilterOptions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getBidderId,
    getBidderIsSuspended,
    isUserDataLoaded,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { allCheckoutErrorsSelector } from '@/redux/modules/checkout/checkout.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { fetchReviewData, getReviewData } from '@/redux/modules/review';
import { fetchPurchasedShippingQuote } from '@/redux/modules/shippingQuote/shippingQuote.actions';
import { changeWonItemsPage, fetchWonItemsIfNeeded } from '@/redux/modules/wonItems/wonItems.actions';
import { getTotalWonItemsCount, getWonItems, getWonItemsPage } from '@/redux/modules/wonItems/wonItems.selectors';
import { ChangeWonItemsPageParams, FetchWonItemsIfNeededParams } from '@/redux/modules/wonItems/wonItems.types';
import { WonCatalog } from '@/types/WonCatalog';
import { parseQueryParams, stringifyQuery, validateQueryParams } from '@/utils/queryParams';
import { getDeskSupportLink, getWonItemsPageUrl } from '@/utils/urls';

import { WonItemsPage } from './WonItemsPage';

type State = {
    hasShownCheckoutErrors: boolean;
};

type WonItemsPageContainerProps = {
    bidderId: number;
    bidderIsSuspended: boolean;
    changeWonItemsPage: (params: ChangeWonItemsPageParams) => void;
    fetchCatalogsIfNeeded: (params: Parameters<typeof fetchCatalogsIfNeeded>[0]) => void;
    fetchPurchasedShippingQuote: (params: Parameters<typeof fetchPurchasedShippingQuote>[0]) => void;
    fetchReviewData: (catalogIds: number[]) => void;
    fetchWonItemsIfNeeded: (params: FetchWonItemsIfNeededParams) => void;
    filter: string;
    filterOptions: any;
    formatMessage: IntlShape['formatMessage'];
    loadInvoiceErrors: { [catalogId: number]: unknown };
    navigate: ReturnType<typeof useNavigate>;
    openSendSellerMessageModal: (
        messageDetails: any,
        sellerId: number,
        sellerName: string,
        subject: string,
        title: string
    ) => void;
    queryParams: any;
    reviews: any[];
    userDataLoaded: boolean;
    userLoggedIn: boolean;
    wonCatalogs: WonCatalog[];

    wonItemsViewData: any;
};

class WonItemsPageContainer extends Component<WonItemsPageContainerProps, State> {
    state = { hasShownCheckoutErrors: false };

    shouldComponentUpdate = (nextProps) => !isEqual(this.props, nextProps);

    componentDidUpdate(prevProps) {
        const {
            bidderId,
            fetchCatalogsIfNeeded,
            fetchReviewData,
            fetchWonItemsIfNeeded,
            filter,
            filterOptions,
            loadInvoiceErrors,
            queryParams,
            reviews,
            userDataLoaded,
            wonCatalogs,
            wonItemsViewData,
        } = this.props;
        const invoiceErrorCatalogIds = Object.keys(loadInvoiceErrors).filter((err) => Boolean(loadInvoiceErrors[err]));
        if (!this.state.hasShownCheckoutErrors && invoiceErrorCatalogIds.length > 0) {
            this.setState({ hasShownCheckoutErrors: true });
            invoiceErrorCatalogIds.forEach((catalogId) => {
                // @ts-expect-error need to convert component to functional
                const message = this.props.formatMessage(
                    {
                        id: 'wonitems.checkoutError',
                    },
                    {
                        catalogId,
                        support: (
                            <Link
                                href={getDeskSupportLink()}
                                target="_blank"
                            >
                                {this.props.formatMessage({
                                    id: 'wonitems.contactSupport',
                                })}
                            </Link>
                        ),
                    }
                ) as string;

                toast('error', {
                    description: message,
                    title: this.props.formatMessage({
                        id: 'toastMessages.titles.success',
                    }),
                });
            });
        }
        const isQueryDiff = filter !== prevProps.filter || !isEqual(queryParams, prevProps.queryParams);
        const nowLoggedIn = !prevProps.userDataLoaded && userDataLoaded;

        if (nowLoggedIn || isQueryDiff) {
            if (filter !== prevProps.filter) {
                this.pushQueryParam({ page: 1 });
                wonItemsViewData.page = 1;
            }
            fetchWonItemsIfNeeded({
                ...wonItemsViewData,
                force: true,
                isDisputed: filter === filterOptions.DISPUTED,
            });
        }

        // If number of won catalogs changes, fetch catalogs and then reviews
        if (bidderId > 0 && !isEqual(prevProps.wonCatalogs, wonCatalogs)) {
            const catalogIds = wonCatalogs.map((c) => c.catalogId);
            Promise.all([fetchCatalogsIfNeeded({ catalogIds }), fetchReviewData(catalogIds)]);
        }

        // if the number of won catalogs is different than the number of reviews
        if (
            (reviews.length !== wonCatalogs.length && wonCatalogs.length) ||
            prevProps.wonCatalogs.length !== wonCatalogs.length
        ) {
            const catalogIds = wonCatalogs.map((c) => c.catalogId);
            fetchReviewData(catalogIds);
        }

        if (!prevProps.wonCatalogs.length && Boolean(wonCatalogs.length)) {
            const catalogIds = wonCatalogs.map((c) => c.catalogId);
            fetchCatalogsIfNeeded({ catalogIds });
        }
    }

    handleFilterChange = ({ value }) => {
        if (this.props.filter !== value) {
            this.pushQueryParam({ filter: value });
        }
    };

    mapFilterOptions = (option: string) => {
        if (!option) {
            return {};
        }
        const label = this.props.formatMessage({ id: option.toLowerCase() });
        return { label, value: option };
    };

    pushQueryParam = (query) => {
        this.props.navigate({
            pathname: getWonItemsPageUrl(),
            search: stringifyQuery({
                ...this.props.queryParams,
                ...query,
            }),
        });
    };

    removeQueryParam = (key) => {
        const query = {
            ...this.props.queryParams,
        };
        delete query[key];

        this.props.navigate({
            pathname: getWonItemsPageUrl(),
            search: stringifyQuery(query),
        });
    };

    render() {
        const { bidderIsSuspended, filter, filterOptions, reviews, wonCatalogs, wonItemsViewData } = this.props;
        const wonFilterOptions = filterOptions ? Object.keys(filterOptions).map(this.mapFilterOptions) : [];

        return (
            <EnsureLoggedInContainer>
                <WonItemsPage
                    bidderIsSuspended={bidderIsSuspended}
                    filter={filter}
                    filterOptions={wonFilterOptions}
                    handleFilterChange={this.handleFilterChange}
                    onPageChange={(params: ChangeWonItemsPageParams) =>
                        this.props.changeWonItemsPage({
                            ...params,
                            isDisputed: this.props.filter === this.props.filterOptions.DISPUTED,
                        })
                    }
                    reviews={reviews}
                    wonCatalogs={wonCatalogs}
                    wonItemsViewData={wonItemsViewData}
                />
            </EnsureLoggedInContainer>
        );
    }
}

/**
 * This function allowed removing react-redux connect and redux-first-history from the class above without rewriting its componentDidUpdate craziness
 */
const WonItemsPageContainerWrapper = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();

    const queryParams: any = parseQueryParams(location.search);
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const userDataLoaded = useAppSelector(isUserDataLoaded);
    const bidderId = useAppSelector(getBidderId);
    const bidderIsSuspended = useAppSelector(getBidderIsSuspended);
    const filterOptions = wonItemsFilterOptions;
    const { filter: queryFilter, page } = validateQueryParams(queryParams, false, 'won', filterOptions);
    const loadInvoiceErrors = useAppSelector(allCheckoutErrorsSelector);

    const reviewData = useAppSelector(getReviewData);
    const reviews = Array.isArray(reviewData) ? reviewData : [reviewData];

    const wonItemsReduxPage = useAppSelector(getWonItemsPage);
    const totalWonItemsCount = useAppSelector(getTotalWonItemsCount);

    const wonItemsViewData = {
        page: wonItemsReduxPage || page || 1,
        pageSize: queryParams.pageSize || 10,
        totalRecords: totalWonItemsCount,
    };
    const filter = queryFilter || wonItemsFilterOptions.ALL;
    const wonCatalogs = useAppSelector(getWonItems);

    return (
        <WonItemsPageContainer
            bidderId={bidderId}
            bidderIsSuspended={bidderIsSuspended}
            changeWonItemsPage={(params: ChangeWonItemsPageParams) => dispatch(changeWonItemsPage(params))}
            fetchCatalogsIfNeeded={(params: Parameters<typeof fetchCatalogsIfNeeded>[0]) =>
                dispatch(fetchCatalogsIfNeeded(params))
            }
            fetchPurchasedShippingQuote={(params: Parameters<typeof fetchPurchasedShippingQuote>[0]) =>
                dispatch(fetchPurchasedShippingQuote(params))
            }
            fetchReviewData={(catalogIds: number[]) => dispatch(fetchReviewData(catalogIds))}
            fetchWonItemsIfNeeded={(params: FetchWonItemsIfNeededParams) => dispatch(fetchWonItemsIfNeeded(params))}
            filter={filter}
            filterOptions={filterOptions}
            formatMessage={formatMessage}
            loadInvoiceErrors={loadInvoiceErrors}
            navigate={navigate}
            openSendSellerMessageModal={(
                messageDetails: any,
                sellerId: number,
                sellerName: string,
                subject: string,
                title: string
            ) => dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject, title))}
            queryParams={queryParams}
            reviews={reviews}
            userDataLoaded={userDataLoaded}
            userLoggedIn={userLoggedIn}
            wonCatalogs={wonCatalogs}
            wonItemsViewData={wonItemsViewData}
        />
    );
};

export default WonItemsPageContainerWrapper;
